import { EffectCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ERequestStatus } from '../../types/requestStatus';
import { TDependencyName, useSensitiveParams } from '../useSensitiveParams';
import { useEffectOnce } from '../useEffectOnce';
import { IEventTrackingData, useEventTracking } from '../useEventTracking';
import { selectRealtorRatingList } from '../../selectors/realtorRatingList';

interface IUseGetRealtorRatingListingOptions {
  dependencyNames?: TDependencyName[];
  eventTrackerData?: IEventTrackingData;
}

/**
 * Выполняет callback для запроса листинга риелторов
 */
export const useGetRealtorRatingListing = (
  callback: EffectCallback,
  {
    dependencyNames = ['realtorRatingPeriod', 'realtorRatingRegion'],
    eventTrackerData,
  }: IUseGetRealtorRatingListingOptions = {},
) => {
  const { status: listingStatus } = useSelector(selectRealtorRatingList);
  const key = useSensitiveParams(dependencyNames);
  const [prevKey, setPrevKey] = useState(key);
  const { trackPageView } = useEventTracking(eventTrackerData);

  /**
   * Однократно трекает открытие листинга.
   */
  useEffectOnce(() => {
    trackPageView();
  });

  /**
   * Вызывает callback, если хотя бы соблюдено одно из условий:
   * - список находится в статусе инициализации
   * - слепок значений из чувствительных ключей изменился
   * Трекает просмотр измененной страницы
   */
  useEffect(() => {
    if (listingStatus !== ERequestStatus.Initial && key === prevKey) {
      return;
    }

    callback();
    setPrevKey(key);
    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, listingStatus, prevKey]);
};
